import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { io as socketIO } from 'socket.io-client';
import {
    Monitor, Laptop, Server, Smartphone,
    Wifi, WifiOff, RefreshCw, Plus, Search,
    Terminal, BarChart3, Settings, Trash2,
    Copy, Check, ChevronRight,
    Cpu, HardDrive, MemoryStick, Clock,
    Tag, Edit3, X, RotateCw,
    Shield, Activity,
    ExternalLink, AlertTriangle,
    MonitorPlay, MousePointer, Eye, EyeOff, Maximize2, Minimize2, Sliders, Lock, KeyRound, Play, StopCircle, Layers, Loader2,
    Globe, Network
} from 'lucide-react';
import { backendURL } from '../config';
import {
    XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Area, AreaChart
} from 'recharts';

// ═══════════════════════════════════════════════════════════════════════
// 🖥️ Machine Connector Dashboard Page
// ═══════════════════════════════════════════════════════════════════════

export default function MachineConnector() {
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [machineDetail, setMachineDetail] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterOS, setFilterOS] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [enrollmentData, setEnrollmentData] = useState(null);
    const [enrollLoading, setEnrollLoading] = useState(false);
    const [enrollServerMode, setEnrollServerMode] = useState('public'); // 'public' | 'lan' | 'custom'
    const [enrollCustomUrl, setEnrollCustomUrl] = useState('http://216.208.91.98:3320');
    const [terminalOutput, setTerminalOutput] = useState('');
    const [terminalInput, setTerminalInput] = useState('');
    const [terminalActive, setTerminalActive] = useState(false);
    const [commandHistory, setCommandHistory] = useState([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [editingName, setEditingName] = useState(false);
    const [newName, setNewName] = useState('');
    const [copiedToken, setCopiedToken] = useState(false);
    const [copiedKey, setCopiedKey] = useState(null);
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [rotatedToken, setRotatedToken] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // ── Remote Desktop (Zoho Assist / LogMeIn style) ──
    const [desktopActive, setDesktopActive] = useState(false);
    const [desktopConnecting, setDesktopConnecting] = useState(false);
    const [desktopControlMode, setDesktopControlMode] = useState('control'); // 'control' | 'view'
    const [desktopFps, setDesktopFps] = useState(10);
    const [desktopQuality, setDesktopQuality] = useState(65);
    const [desktopScale, setDesktopScale] = useState(0.75);
    const [remoteRes, setRemoteRes] = useState({ width: 1920, height: 1080 });
    const [liveFps, setLiveFps] = useState(0);
    const [latency, setLatency] = useState(0);
    const [isDesktopFullscreen, setIsDesktopFullscreen] = useState(false);
    const [desktopError, setDesktopError] = useState(null);
    const [isRemoteLocked, setIsRemoteLocked] = useState(false);
    const [showUnlockModal, setShowUnlockModal] = useState(false);
    const [unlockPassword, setUnlockPassword] = useState('');
    const [showUnlockPassword, setShowUnlockPassword] = useState(false);
    const [unlocking, setUnlocking] = useState(false);
    const [unlockStatus, setUnlockStatus] = useState(null);

    const desktopCanvasRef = useRef(null);
    const desktopContainerRef = useRef(null);
    const frameCountRef = useRef(0);
    const lastFpsCalcRef = useRef(Date.now());
    const lastMouseMoveRef = useRef(0);
    const desktopTimeoutRef = useRef(null);

    const terminalRef = useRef(null);
    const terminalInputRef = useRef(null);
    const socketRef = useRef(null);

    // ── Fetch machines ──
    const fetchMachines = useCallback(async () => {
        try {
            const res = await axios.get(`${backendURL}/api/soc/machines`);
            if (res.data?.success) {
                setMachines(res.data.machines);
            }
        } catch (err) {
            console.error("Failed to fetch machines:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    // ── Fetch single machine detail ──
    const fetchMachineDetail = useCallback(async (machineId) => {
        try {
            const res = await axios.get(`${backendURL}/api/soc/machines/${machineId}`);
            if (res.data?.success) {
                setMachineDetail(res.data.machine);
            }
        } catch (err) {
            console.error("Failed to fetch machine detail:", err);
        }
    }, []);

    // ── Socket.IO for real-time updates ──
    useEffect(() => {
        fetchMachines();

        const token = localStorage.getItem("token");
        const socket = socketIO(backendURL || window.location.origin, {
            auth: { token },
            transports: ['websocket', 'polling']
        });
        socketRef.current = socket;

        socket.on('machine:status-update', (data) => {
            setMachines(prev => prev.map(m =>
                m.machineId === data.machineId
                    ? { ...m, status: data.status, lastSeen: data.timestamp }
                    : m
            ));
        });

        socket.on('machine:heartbeat', (data) => {
            setMachines(prev => prev.map(m =>
                m.machineId === data.machineId
                    ? {
                        ...m,
                        status: 'online',
                        lastSeen: data.timestamp,
                        cpu: { ...m.cpu, usage: data.cpuUsage },
                        memory: { ...m.memory, usagePercent: data.memoryUsagePercent }
                    }
                    : m
            ));
        });

        const interval = setInterval(fetchMachines, 30000);

        return () => {
            socket.disconnect();
            clearInterval(interval);
        };
    }, [fetchMachines]);

    // ── Listen for terminal output for selected machine ──
    useEffect(() => {
        if (!selectedMachine || !socketRef.current) return;

        const handleTermData = (data) => {
            setTerminalOutput(prev => prev + data.data);
            scrollTerminal();
        };

        const handleCmdOutput = (data) => {
            setTerminalOutput(prev => prev + data.output);
            scrollTerminal();
        };

        const termChannel = `machine:terminal-data:${selectedMachine}`;
        const cmdChannel = `machine:command-output:${selectedMachine}`;

        socketRef.current.on(termChannel, handleTermData);
        socketRef.current.on(cmdChannel, handleCmdOutput);

        return () => {
            socketRef.current.off(termChannel, handleTermData);
            socketRef.current.off(cmdChannel, handleCmdOutput);
        };
    }, [selectedMachine]);

    // ── Remote Desktop Socket Handlers ──
    useEffect(() => {
        if (!selectedMachine || !socketRef.current) return;

        const frameChannel = `machine:desktop-frame:${selectedMachine}`;
        const stoppedChannel = `machine:desktop-stopped:${selectedMachine}`;

        const handleFrame = (data) => {
            if (desktopTimeoutRef.current) {
                clearTimeout(desktopTimeoutRef.current);
                desktopTimeoutRef.current = null;
            }
            setDesktopConnecting(false);
            setDesktopActive(true);
            setDesktopError(null);
            setIsRemoteLocked(Boolean(data.isLocked));

            if (data.width && data.height) {
                setRemoteRes({ width: data.width, height: data.height });
            }

            if (data.timestamp) {
                const now = Date.now();
                setLatency(Math.max(0, now - data.timestamp));
            }

            // FPS counter
            frameCountRef.current += 1;
            const now = Date.now();
            if (now - lastFpsCalcRef.current >= 1000) {
                setLiveFps(frameCountRef.current);
                frameCountRef.current = 0;
                lastFpsCalcRef.current = now;
            }

            // Render frame to canvas
            const canvas = desktopCanvasRef.current;
            if (canvas && data.image) {
                const ctx = canvas.getContext('2d');
                const img = new Image();
                img.onload = () => {
                    if (canvas.width !== img.width || canvas.height !== img.height) {
                        canvas.width = img.width;
                        canvas.height = img.height;
                    }
                    ctx.drawImage(img, 0, 0);
                };
                img.src = data.image;
            }
        };

        const handleStopped = (data) => {
            if (desktopTimeoutRef.current) {
                clearTimeout(desktopTimeoutRef.current);
                desktopTimeoutRef.current = null;
            }
            setDesktopActive(false);
            setDesktopConnecting(false);
            setIsRemoteLocked(false);
            if (data?.error) {
                setDesktopError(data.error);
            }
        };

        const unlockResultChannel = `machine:desktop-unlock-result:${selectedMachine}`;
        const handleUnlockResult = (data) => {
            setUnlocking(false);
            if (data?.success) {
                setIsRemoteLocked(false);
                setUnlockStatus({ type: 'success', message: 'Workstation successfully unlocked!' });
                setTimeout(() => {
                    setShowUnlockModal(false);
                    setUnlockPassword('');
                    setUnlockStatus(null);
                }, 1200);
            } else {
                setUnlockStatus({ type: 'error', message: data?.message || 'Unlock attempt finished. Please verify password.' });
            }
        };

        socketRef.current.on(frameChannel, handleFrame);
        socketRef.current.on(stoppedChannel, handleStopped);
        socketRef.current.on(unlockResultChannel, handleUnlockResult);

        return () => {
            if (desktopTimeoutRef.current) {
                clearTimeout(desktopTimeoutRef.current);
                desktopTimeoutRef.current = null;
            }
            socketRef.current.off(frameChannel, handleFrame);
            socketRef.current.off(stoppedChannel, handleStopped);
            socketRef.current.off(unlockResultChannel, handleUnlockResult);
        };
    }, [selectedMachine]);

    // ── Desktop Control Actions ──
    const startDesktopSession = async () => {
        if (!selectedMachine) return;
        setDesktopConnecting(true);
        setDesktopError(null);

        // Safeguard: 15-second connection timeout
        if (desktopTimeoutRef.current) clearTimeout(desktopTimeoutRef.current);
        desktopTimeoutRef.current = setTimeout(() => {
            setDesktopConnecting(false);
            setDesktopError("Connection timed out. Remote endpoint did not return desktop screen within 15 seconds. Ensure the endpoint is online and a user is logged into the console.");
        }, 15000);

        try {
            const streamSettings = {
                fps: desktopFps,
                quality: desktopQuality,
                scale: desktopScale
            };

            if (socketRef.current && socketRef.current.connected) {
                socketRef.current.emit('machine:desktop-start', {
                    machineId: selectedMachine,
                    settings: streamSettings
                });
            } else {
                // Fallback to REST only if socket is not actively connected
                axios.post(`${backendURL}/api/soc/machines/${selectedMachine}/desktop/start`, {
                    settings: streamSettings
                }).catch(err => {
                    console.warn("REST desktop start notice:", err.response?.data?.error || err.message);
                });
            }
        } catch (err) {
            if (desktopTimeoutRef.current) {
                clearTimeout(desktopTimeoutRef.current);
                desktopTimeoutRef.current = null;
            }
            setDesktopConnecting(false);
            setDesktopError(err.message);
        }
    };

    const stopDesktopSession = async () => {
        if (!selectedMachine) return;
        if (desktopTimeoutRef.current) {
            clearTimeout(desktopTimeoutRef.current);
            desktopTimeoutRef.current = null;
        }
        setDesktopActive(false);
        setDesktopConnecting(false);
        try {
            socketRef.current?.emit('machine:desktop-stop', { machineId: selectedMachine });
        } catch (err) {
            console.error("Stop desktop error:", err);
        }
    };

    const sendDesktopCAD = async (action) => {
        if (!selectedMachine) return;
        try {
            await axios.post(`${backendURL}/api/soc/machines/${selectedMachine}/desktop/cad`, { action });
            socketRef.current?.emit('machine:desktop-cad', { machineId: selectedMachine, action });
        } catch (err) {}
    };

    const sendDesktopUnlock = async (password) => {
        if (!selectedMachine) return;
        setUnlocking(true);
        setUnlockStatus({ type: 'info', message: 'Waking screen and injecting credentials...' });
        try {
            socketRef.current?.emit('machine:desktop-unlock', { machineId: selectedMachine, password });
            const res = await axios.post(`${backendURL}/api/soc/machines/${selectedMachine}/desktop/unlock`, { password });
            if (!res.data?.success) {
                setUnlockStatus({ type: 'error', message: res.data?.error || 'Failed to dispatch unlock' });
                setUnlocking(false);
            }
        } catch (err) {
            setUnlockStatus({ type: 'error', message: err.response?.data?.error || err.message });
            setUnlocking(false);
        }
    };

    const updateDesktopSettings = (newSettings) => {
        if (!selectedMachine) return;
        socketRef.current?.emit('machine:desktop-settings', {
            machineId: selectedMachine,
            ...newSettings
        });
    };

    const handleDesktopMouseEvent = (e, action) => {
        if (desktopControlMode !== 'control' || !desktopActive) return;
        const canvas = desktopCanvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const clientX = e.clientX - rect.left;
        const clientY = e.clientY - rect.top;

        // Map canvas coordinates to remote desktop coordinates
        const scaleX = remoteRes.width / rect.width;
        const scaleY = remoteRes.height / rect.height;

        const remoteX = Math.round(clientX * scaleX);
        const remoteY = Math.round(clientY * scaleY);

        if (action === 'move') {
            const now = Date.now();
            if (now - lastMouseMoveRef.current < 25) return;
            lastMouseMoveRef.current = now;
        }

        let button = 'left';
        if (e.button === 2) button = 'right';
        else if (e.button === 1) button = 'middle';

        socketRef.current?.emit('machine:desktop-mouse', {
            machineId: selectedMachine,
            action,
            x: remoteX,
            y: remoteY,
            button
        });
    };

    const handleDesktopWheel = (e) => {
        if (desktopControlMode !== 'control' || !desktopActive) return;
        e.preventDefault();
        const delta = -Math.sign(e.deltaY) * 120;
        socketRef.current?.emit('machine:desktop-mouse', {
            machineId: selectedMachine,
            action: 'wheel',
            delta
        });
    };

    const handleDesktopKeyDown = (e) => {
        if (desktopControlMode !== 'control' || !desktopActive) return;
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

        if (['Tab', 'F5', 'Alt', 'Control', 'Meta'].includes(e.key) || (e.ctrlKey && ['r', 'w', 'f'].includes(e.key.toLowerCase()))) {
            e.preventDefault();
        }

        socketRef.current?.emit('machine:desktop-key', {
            machineId: selectedMachine,
            action: 'down',
            key: e.key,
            code: e.code
        });
    };

    const toggleDesktopFullscreen = () => {
        if (!desktopContainerRef.current) return;
        if (!document.fullscreenElement) {
            desktopContainerRef.current.requestFullscreen?.().then(() => setIsDesktopFullscreen(true)).catch(() => {});
        } else {
            document.exitFullscreen?.().then(() => setIsDesktopFullscreen(false)).catch(() => {});
        }
    };

    // ── Select machine ──
    const handleSelectMachine = (machine) => {
        if (desktopActive) {
            stopDesktopSession();
        }
        setSelectedMachine(machine.machineId);
        setActiveTab('overview');
        setTerminalOutput('');
        setTerminalActive(false);
        fetchMachineDetail(machine.machineId);
    };

    // ── Terminal ──
    const scrollTerminal = () => {
        setTimeout(() => {
            if (terminalRef.current) {
                terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
            }
        }, 50);
    };

    const startTerminal = async () => {
        try {
            await axios.post(`${backendURL}/api/soc/machines/${selectedMachine}/terminal/start`, { cols: 120, rows: 30 });
            setTerminalActive(true);
            setTerminalOutput('\x1b[32m[Sentinel Terminal]\x1b[0m Connected to remote machine...\r\n\r\n');
        } catch (err) {
            setTerminalOutput(`\x1b[31mError: ${err.response?.data?.error || err.message}\x1b[0m\r\n`);
        }
    };

    const sendCommand = async () => {
        if (!terminalInput.trim()) return;
        const cmd = terminalInput.trim();
        setCommandHistory(prev => [...prev, cmd]);
        setHistoryIndex(-1);

        setTerminalOutput(prev => prev + `\x1b[36m$ ${cmd}\x1b[0m\r\n`);
        setTerminalInput('');

        if (terminalActive) {
            await axios.post(`${backendURL}/api/soc/machines/${selectedMachine}/terminal/input`, { data: cmd + '\n' });
        } else {
            try {
                await axios.post(`${backendURL}/api/soc/machines/${selectedMachine}/command`, { command: cmd });
            } catch (err) {
                setTerminalOutput(prev => prev + `\x1b[31mError: ${err.response?.data?.error || err.message}\x1b[0m\r\n`);
            }
        }
        scrollTerminal();
    };

    const handleTerminalKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            sendCommand();
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (commandHistory.length > 0) {
                const newIndex = historyIndex < commandHistory.length - 1 ? historyIndex + 1 : historyIndex;
                setHistoryIndex(newIndex);
                setTerminalInput(commandHistory[commandHistory.length - 1 - newIndex]);
            }
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (historyIndex > 0) {
                const newIndex = historyIndex - 1;
                setHistoryIndex(newIndex);
                setTerminalInput(commandHistory[commandHistory.length - 1 - newIndex]);
            } else {
                setHistoryIndex(-1);
                setTerminalInput('');
            }
        }
    };

    // ── Enrollment ──
    const generateEnrollmentToken = async () => {
        setEnrollLoading(true);
        try {
            const res = await axios.post(`${backendURL}/api/soc/machines/generate-token`, {
                name: `Machine-${Date.now().toString(36).toUpperCase()}`,
                enrolledBy: 'admin'
            });
            if (res.data?.success) {
                setEnrollmentData(res.data);
                setShowEnrollModal(true);
            }
        } catch (err) {
            console.error("Token generation failed:", err);
        } finally {
            setEnrollLoading(false);
        }
    };

    // ── Machine actions ──
    const renameMachine = async () => {
        if (!newName.trim() || !selectedMachine) return;
        try {
            await axios.patch(`${backendURL}/api/soc/machines/${selectedMachine}`, { customName: newName.trim() });
            setEditingName(false);
            fetchMachines();
            fetchMachineDetail(selectedMachine);
        } catch (err) {
            console.error("Rename failed:", err);
        }
    };

    const deleteMachine = async () => {
        if (!selectedMachine) return;
        try {
            await axios.delete(`${backendURL}/api/soc/machines/${selectedMachine}`);
            setSelectedMachine(null);
            setMachineDetail(null);
            setShowDeleteConfirm(false);
            fetchMachines();
        } catch (err) {
            console.error("Delete failed:", err);
        }
    };

    const rotateToken = async () => {
        if (!selectedMachine) return;
        try {
            const res = await axios.post(`${backendURL}/api/soc/machines/${selectedMachine}/rotate-token`);
            if (res.data?.success) {
                setRotatedToken(res.data.newMachineToken);
                setShowTokenModal(true);
            }
        } catch (err) {
            console.error("Token rotation failed:", err);
        }
    };

    const downloadRDP = () => {
        if (!selectedMachine) return;
        const token = localStorage.getItem("token") || "";
        window.open(`${backendURL}/api/soc/machines/${selectedMachine}/rdp-file?token=${encodeURIComponent(token)}`, '_blank');
    };

    // ── Safe clipboard copy helper (works in all HTTP / HTTPS / remote IP / iframe contexts) ──
    const copyToClipboard = async (text, key = 'token') => {
        if (!text) return;
        let success = false;
        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                await navigator.clipboard.writeText(text);
                success = true;
            }
        } catch (err) {
            console.warn("navigator.clipboard.writeText unavailable or rejected, using execCommand fallback:", err);
        }

        if (!success) {
            try {
                const textarea = document.createElement("textarea");
                textarea.value = text;
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                textarea.style.top = "-9999px";
                textarea.style.opacity = "0";
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                success = document.execCommand("copy");
                document.body.removeChild(textarea);
            } catch (e) {
                console.error("Fallback execCommand copy failed:", e);
            }
        }

        setCopiedKey(key);
        setCopiedToken(true);
        setTimeout(() => {
            setCopiedKey(null);
            setCopiedToken(false);
        }, 2000);
    };

    // ── Helpers ──
    const getOSIcon = (platform) => {
        switch (platform) {
            case 'win32': return <Monitor className="w-4 h-4" />;
            case 'darwin': return <Laptop className="w-4 h-4" />;
            case 'linux': return <Server className="w-4 h-4" />;
            default: return <Smartphone className="w-4 h-4" />;
        }
    };

    const getOSLabel = (os) => {
        if (!os) return 'Unknown';
        if (os.platform === 'win32') return os.distro || 'Windows';
        if (os.platform === 'darwin') return os.distro || 'macOS';
        if (os.platform === 'linux') return os.distro || 'Linux';
        return os.platform || 'Unknown';
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    };

    const formatUptime = (seconds) => {
        if (!seconds) return '—';
        const days = Math.floor(seconds / 86400);
        const hours = Math.floor((seconds % 86400) / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${mins}m`;
        return `${mins}m`;
    };

    const timeAgo = (dateStr) => {
        if (!dateStr) return 'Never';
        const diff = Date.now() - new Date(dateStr).getTime();
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return `${Math.floor(diff / 86400000)}d ago`;
    };

    // ── Filtering ──
    const filteredMachines = machines.filter(m => {
        if (filterStatus !== 'all' && m.status !== filterStatus) return false;
        if (filterOS !== 'all' && m.os?.platform !== filterOS) return false;
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (
                (m.hostname || '').toLowerCase().includes(q) ||
                (m.customName || '').toLowerCase().includes(q) ||
                (m.ipAddresses || []).some(ip => ip.includes(q)) ||
                (m.machineId || '').includes(q)
            );
        }
        return true;
    });

    const onlineCount = machines.filter(m => m.status === 'online').length;
    const offlineCount = machines.filter(m => m.status === 'offline').length;
    const currentMachine = machines.find(m => m.machineId === selectedMachine);

    // ═══════════════════════════════════════════════════════════════════════
    // 🎨 RENDER
    // ═══════════════════════════════════════════════════════════════════════
    return (
        <div className="min-h-screen bg-[#070b14] text-slate-100 p-6 space-y-6">
            {/* ── Header ── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.08] pb-5">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-cyan-600/10 border border-cyan-500/20 text-cyan-400">
                        <Monitor className="w-6 h-6" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2.5">
                            <h1 className="text-xl font-black text-slate-100 tracking-tight">
                                Machine Connector
                            </h1>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 font-mono">
                                ENDPOINT MGMT
                            </span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 font-mono">
                            Remote machine enrollment, monitoring &amp; administration
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Stats chips */}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                        <Wifi className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-xs font-bold text-emerald-400 font-mono">{onlineCount}</span>
                        <span className="text-[10px] text-emerald-400/60">Online</span>
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-500/10 border border-rose-500/20">
                        <WifiOff className="w-3.5 h-3.5 text-rose-400" />
                        <span className="text-xs font-bold text-rose-400 font-mono">{offlineCount}</span>
                        <span className="text-[10px] text-rose-400/60">Offline</span>
                    </div>

                    <button
                        onClick={fetchMachines}
                        className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-slate-400 hover:text-slate-200 transition-all border border-white/[0.06]"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>

                    <button
                        onClick={generateEnrollmentToken}
                        disabled={enrollLoading}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-semibold transition-all shadow-lg shadow-cyan-600/20"
                    >
                        <Plus className="w-4 h-4" />
                        Enroll Machine
                    </button>
                </div>
            </div>

            {/* ── Main Layout ── */}
            <div className="flex gap-6 min-h-[calc(100vh-200px)]">
                {/* ── Left: Machine List ── */}
                <div className={`${selectedMachine ? 'w-[340px]' : 'w-full'} flex flex-col gap-4 transition-all duration-300`}>
                    {/* Search & Filters */}
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input
                                type="text"
                                placeholder="Search machines..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/40 transition-colors"
                            />
                        </div>
                        <select
                            value={filterOS}
                            onChange={e => setFilterOS(e.target.value)}
                            className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-slate-300 focus:outline-none cursor-pointer"
                        >
                            <option value="all">All OS</option>
                            <option value="win32">Windows</option>
                            <option value="linux">Linux</option>
                            <option value="darwin">macOS</option>
                        </select>
                        <select
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                            className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-xs text-slate-300 focus:outline-none cursor-pointer"
                        >
                            <option value="all">All Status</option>
                            <option value="online">Online</option>
                            <option value="offline">Offline</option>
                        </select>
                    </div>

                    {/* Machine Cards */}
                    <div className="space-y-2 overflow-y-auto max-h-[calc(100vh-300px)] pr-1 scrollbar-thin">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
                                <RefreshCw className="w-8 h-8 animate-spin mb-3" />
                                <span className="text-sm">Loading machines...</span>
                            </div>
                        ) : filteredMachines.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-slate-600">
                                <Monitor className="w-12 h-12 mb-3 opacity-30" />
                                <span className="text-sm font-medium">No machines found</span>
                                <span className="text-xs mt-1">Click "Enroll Machine" to add one</span>
                            </div>
                        ) : (
                            filteredMachines.map(machine => (
                                <button
                                    key={machine.machineId}
                                    onClick={() => handleSelectMachine(machine)}
                                    className={`w-full text-left p-4 rounded-xl border transition-all duration-200 group ${
                                        selectedMachine === machine.machineId
                                            ? 'bg-cyan-500/10 border-cyan-500/30 shadow-lg shadow-cyan-500/5'
                                            : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.10]'
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        {/* OS Icon + Status */}
                                        <div className={`p-2 rounded-lg ${
                                            machine.status === 'online'
                                                ? 'bg-emerald-500/10 text-emerald-400'
                                                : 'bg-slate-500/10 text-slate-500'
                                        }`}>
                                            {getOSIcon(machine.os?.platform)}
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-slate-200 truncate">
                                                    {machine.customName || machine.hostname}
                                                </span>
                                                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                                    machine.status === 'online'
                                                        ? 'bg-emerald-400 shadow-lg shadow-emerald-400/50 animate-pulse'
                                                        : machine.status === 'enrolling'
                                                        ? 'bg-amber-400 animate-pulse'
                                                        : 'bg-slate-600'
                                                }`} />
                                            </div>

                                            <div className="flex items-center gap-2 mt-1">
                                                <span className="text-[10px] font-mono text-slate-500">
                                                    {getOSLabel(machine.os)}
                                                </span>
                                                {machine.ipAddresses?.[0] && (
                                                    <span className="text-[10px] font-mono text-slate-600">
                                                        {machine.ipAddresses[0]}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Resource bars (only when not selected/expanded) */}
                                            {machine.status === 'online' && (
                                                <div className="flex gap-3 mt-2">
                                                    <div className="flex-1">
                                                        <div className="flex justify-between text-[9px] font-mono text-slate-600 mb-0.5">
                                                            <span>CPU</span>
                                                            <span>{(machine.cpu?.usage || 0).toFixed(0)}%</span>
                                                        </div>
                                                        <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
                                                                style={{ width: `${machine.cpu?.usage || 0}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex justify-between text-[9px] font-mono text-slate-600 mb-0.5">
                                                            <span>RAM</span>
                                                            <span>{(machine.memory?.usagePercent || 0).toFixed(0)}%</span>
                                                        </div>
                                                        <div className="h-1 bg-white/[0.06] rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-500"
                                                                style={{ width: `${machine.memory?.usagePercent || 0}%` }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-slate-400 flex-shrink-0 mt-0.5" />
                                    </div>

                                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/[0.04]">
                                        <span className="text-[9px] font-mono text-slate-600">
                                            Last seen: {timeAgo(machine.lastSeen)}
                                        </span>
                                        {machine.tags?.length > 0 && (
                                            <div className="flex gap-1">
                                                {machine.tags.slice(0, 2).map((tag, i) => (
                                                    <span key={i} className="px-1.5 py-0.5 rounded text-[8px] font-mono bg-white/[0.04] text-slate-500">
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* ── Right: Machine Detail Panel ── */}
                {selectedMachine && currentMachine && (
                    <div className="flex-1 bg-white/[0.02] rounded-2xl border border-white/[0.06] overflow-hidden flex flex-col">
                        {/* Detail Header */}
                        <div className="p-5 border-b border-white/[0.06] bg-gradient-to-r from-white/[0.02] to-transparent">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className={`p-3 rounded-xl ${
                                        currentMachine.status === 'online'
                                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                            : 'bg-slate-500/10 text-slate-500 border border-slate-500/20'
                                    }`}>
                                        {getOSIcon(currentMachine.os?.platform)}
                                    </div>
                                    <div>
                                        {editingName ? (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    value={newName}
                                                    onChange={e => setNewName(e.target.value)}
                                                    onKeyDown={e => e.key === 'Enter' && renameMachine()}
                                                    className="bg-white/[0.06] border border-white/[0.12] rounded px-2 py-1 text-sm text-slate-200 focus:outline-none focus:border-cyan-500/40"
                                                    autoFocus
                                                />
                                                <button onClick={renameMachine} className="text-emerald-400 hover:text-emerald-300"><Check className="w-4 h-4" /></button>
                                                <button onClick={() => setEditingName(false)} className="text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-2">
                                                <h2 className="text-lg font-bold text-slate-100">
                                                    {currentMachine.customName || currentMachine.hostname}
                                                </h2>
                                                <button
                                                    onClick={() => { setEditingName(true); setNewName(currentMachine.customName || currentMachine.hostname); }}
                                                    className="text-slate-600 hover:text-slate-400"
                                                >
                                                    <Edit3 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold font-mono ${
                                                currentMachine.status === 'online'
                                                    ? 'bg-emerald-500/20 text-emerald-400'
                                                    : 'bg-slate-500/20 text-slate-500'
                                            }`}>
                                                {currentMachine.status === 'online' ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                                                {currentMachine.status.toUpperCase()}
                                            </span>
                                            <span className="text-[10px] font-mono text-slate-500">
                                                {getOSLabel(currentMachine.os)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-1.5">
                                    {currentMachine.status === 'online' && (
                                        <button
                                            onClick={() => {
                                                setActiveTab('desktop');
                                                if (!desktopActive && !desktopConnecting) {
                                                    startDesktopSession();
                                                }
                                            }}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                                                activeTab === 'desktop'
                                                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shadow-sm shadow-emerald-500/20'
                                                    : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border-emerald-500/20'
                                            }`}
                                        >
                                            <MonitorPlay className="w-3.5 h-3.5" />
                                            Remote Desktop
                                        </button>
                                    )}
                                    {currentMachine.os?.platform === 'win32' && currentMachine.status === 'online' && (
                                        <button
                                            onClick={downloadRDP}
                                            title="Download Windows RDP Connection File"
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 text-xs font-semibold border border-blue-500/20 transition-all"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                            RDP File
                                        </button>
                                    )}
                                    <button
                                        onClick={() => {
                                            if (desktopActive) stopDesktopSession();
                                            setSelectedMachine(null);
                                            setMachineDetail(null);
                                        }}
                                        className="p-2 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-white/[0.06] transition-all"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex border-b border-white/[0.06] bg-white/[0.01]">
                            {[
                                { id: 'overview', label: 'Overview', icon: Activity },
                                { id: 'desktop', label: 'Remote Desktop', icon: MonitorPlay },
                                { id: 'terminal', label: 'Terminal', icon: Terminal },
                                { id: 'resources', label: 'Resources', icon: BarChart3 },
                                { id: 'settings', label: 'Settings', icon: Settings }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-1.5 px-5 py-3 text-xs font-semibold transition-all border-b-2 ${
                                        activeTab === tab.id
                                            ? 'text-cyan-400 border-cyan-400 bg-cyan-500/5'
                                            : 'text-slate-500 border-transparent hover:text-slate-300 hover:bg-white/[0.02]'
                                    }`}
                                >
                                    <tab.icon className="w-3.5 h-3.5" />
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Tab Content */}
                        <div className="flex-1 overflow-y-auto p-5">
                            {/* ── Overview Tab ── */}
                            {activeTab === 'overview' && (
                                <div className="space-y-5">
                                    {/* Quick Stats */}
                                    <div className="grid grid-cols-4 gap-3">
                                        {[
                                            { label: 'CPU', value: `${(currentMachine.cpu?.usage || 0).toFixed(1)}%`, icon: Cpu, color: 'cyan' },
                                            { label: 'Memory', value: `${(currentMachine.memory?.usagePercent || 0).toFixed(1)}%`, icon: MemoryStick, color: 'purple' },
                                            { label: 'Disk', value: `${(currentMachine.disk?.usagePercent || 0).toFixed(1)}%`, icon: HardDrive, color: 'amber' },
                                            { label: 'Uptime', value: formatUptime(currentMachine.uptime), icon: Clock, color: 'emerald' }
                                        ].map(stat => (
                                            <div key={stat.label} className={`p-3 rounded-xl bg-${stat.color}-500/5 border border-${stat.color}-500/10`}>
                                                <div className="flex items-center gap-1.5 mb-1">
                                                    <stat.icon className={`w-3.5 h-3.5 text-${stat.color}-400`} />
                                                    <span className="text-[10px] font-mono text-slate-500 uppercase">{stat.label}</span>
                                                </div>
                                                <span className={`text-lg font-black text-${stat.color}-400 font-mono`}>{stat.value}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* System Info Grid */}
                                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
                                        <div className="px-4 py-2.5 border-b border-white/[0.06] bg-white/[0.02]">
                                            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">System Information</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-px bg-white/[0.04]">
                                            {[
                                                { label: 'Hostname', value: currentMachine.hostname },
                                                { label: 'Machine ID', value: currentMachine.machineId?.slice(0, 12) + '...' },
                                                { label: 'Operating System', value: getOSLabel(currentMachine.os) },
                                                { label: 'Architecture', value: currentMachine.os?.arch || '—' },
                                                { label: 'CPU', value: currentMachine.cpu?.model || '—' },
                                                { label: 'CPU Cores', value: currentMachine.cpu?.cores || '—' },
                                                { label: 'Total Memory', value: formatBytes(currentMachine.memory?.total) },
                                                { label: 'Total Disk', value: formatBytes(currentMachine.disk?.total) },
                                                { label: 'IP Addresses', value: (currentMachine.ipAddresses || []).join(', ') || '—' },
                                                { label: 'Agent Version', value: currentMachine.agentVersion || '—' },
                                                { label: 'Enrolled', value: currentMachine.enrolledAt ? new Date(currentMachine.enrolledAt).toLocaleDateString() : '—' },
                                                { label: 'Last Seen', value: timeAgo(currentMachine.lastSeen) }
                                            ].map(item => (
                                                <div key={item.label} className="bg-[#070b14] px-4 py-2.5">
                                                    <span className="text-[10px] font-mono text-slate-600 uppercase block">{item.label}</span>
                                                    <span className="text-xs font-medium text-slate-300 mt-0.5 block truncate">{item.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* ── Remote Desktop Tab ── */}
                            {activeTab === 'desktop' && (
                                <div className="h-full flex flex-col gap-3 min-h-[560px]">
                                    {currentMachine.status !== 'online' ? (
                                        <div className="flex-1 flex flex-col items-center justify-center text-slate-600 p-8">
                                            <WifiOff className="w-12 h-12 mb-3 opacity-30 text-rose-400" />
                                            <span className="text-sm font-semibold text-slate-300">Remote Endpoint Offline</span>
                                            <span className="text-xs text-slate-500 mt-1 max-w-sm text-center">
                                                The agent must be running and connected to stream the desktop screen. Once the machine boots or reconnects, live control will be available instantly.
                                            </span>
                                        </div>
                                    ) : !desktopActive && !desktopConnecting ? (
                                        <div className="flex-1 flex items-center justify-center p-4">
                                            <div className="max-w-xl w-full rounded-2xl bg-gradient-to-b from-white/[0.04] to-white/[0.01] border border-white/[0.08] p-8 text-center shadow-2xl backdrop-blur-xl">
                                                <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-emerald-500/10">
                                                    <MonitorPlay className="w-8 h-8 text-emerald-400" />
                                                </div>

                                                <h3 className="text-lg font-bold text-white mb-2">Live Remote Desktop & Screen Control</h3>
                                                <p className="text-xs text-slate-400 mb-6 leading-relaxed">
                                                    View and control <span className="text-cyan-400 font-mono font-semibold">{currentMachine.hostname}</span> directly in your browser. Bypasses NAT & firewalls with zero extra ports or VPN required.
                                                </p>

                                                {/* Streaming Quality Presets */}
                                                <div className="grid grid-cols-3 gap-3 mb-6 text-left">
                                                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                                                        <span className="text-[10px] font-mono text-slate-500 uppercase block mb-1">FPS Target</span>
                                                        <select
                                                            value={desktopFps}
                                                            onChange={e => setDesktopFps(Number(e.target.value))}
                                                            className="w-full bg-[#0d1322] border border-white/[0.1] rounded-lg px-2 py-1.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-cyan-500/40"
                                                        >
                                                            <option value={5}>5 FPS (Low Bandwidth)</option>
                                                            <option value={10}>10 FPS (Smooth Balanced)</option>
                                                            <option value={15}>15 FPS (High Performance)</option>
                                                        </select>
                                                    </div>

                                                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                                                        <span className="text-[10px] font-mono text-slate-500 uppercase block mb-1">Compression Quality</span>
                                                        <select
                                                            value={desktopQuality}
                                                            onChange={e => setDesktopQuality(Number(e.target.value))}
                                                            className="w-full bg-[#0d1322] border border-white/[0.1] rounded-lg px-2 py-1.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-cyan-500/40"
                                                        >
                                                            <option value={40}>Eco (40%)</option>
                                                            <option value={65}>Balanced (65%)</option>
                                                            <option value={85}>Crisp / Sharp (85%)</option>
                                                        </select>
                                                    </div>

                                                    <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                                                        <span className="text-[10px] font-mono text-slate-500 uppercase block mb-1">Resolution Scale</span>
                                                        <select
                                                            value={desktopScale}
                                                            onChange={e => setDesktopScale(Number(e.target.value))}
                                                            className="w-full bg-[#0d1322] border border-white/[0.1] rounded-lg px-2 py-1.5 text-xs text-slate-200 font-mono focus:outline-none focus:border-cyan-500/40"
                                                        >
                                                            <option value={0.5}>50% (Fast)</option>
                                                            <option value={0.75}>75% (Recommended)</option>
                                                            <option value={1.0}>100% (Native)</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Launch Action */}
                                                <button
                                                    onClick={startDesktopSession}
                                                    className="w-full py-3.5 px-6 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 group"
                                                >
                                                    <MonitorPlay className="w-4 h-4 group-hover:scale-110 transition-transform" />
                                                    Launch Remote Desktop Session
                                                </button>

                                                {desktopError && (
                                                    <div className="mt-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs flex items-center gap-2 text-left">
                                                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                                        <span>{desktopError}</span>
                                                    </div>
                                                )}

                                                <div className="grid grid-cols-2 gap-3 mt-6 pt-6 border-t border-white/[0.06] text-left">
                                                    <div className="flex items-start gap-2">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                                                        <span className="text-[11px] text-slate-400">Full mouse click, move, drag & wheel events</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                                                        <span className="text-[11px] text-slate-400">Collaborative session (user is not logged out)</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                                                        <span className="text-[11px] text-slate-400">Send Ctrl+Alt+Del, Start Menu & Task Manager</span>
                                                    </div>
                                                    <div className="flex items-start gap-2">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                                                        <span className="text-[11px] text-slate-400">Instant toggle between Control & View-Only</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : desktopConnecting ? (
                                        <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                                            <div className="relative mb-6">
                                                <div className="w-16 h-16 rounded-full border-2 border-emerald-500/20 border-t-emerald-400 animate-spin" />
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <MonitorPlay className="w-6 h-6 text-emerald-400 animate-pulse" />
                                                </div>
                                            </div>
                                            <h4 className="text-sm font-semibold text-slate-200">Connecting to Remote Desktop...</h4>
                                            <p className="text-xs text-slate-500 mt-1 max-w-xs">
                                                Requesting screen stream from <span className="text-cyan-400 font-mono">{currentMachine.hostname}</span>
                                            </p>
                                            {desktopError && (
                                                <div className="mt-4 p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs flex items-center gap-2 text-left max-w-md">
                                                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                                                    <span>{desktopError}</span>
                                                </div>
                                            )}
                                            <button
                                                onClick={stopDesktopSession}
                                                className="mt-6 px-4 py-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] text-slate-400 hover:text-slate-200 text-xs font-semibold transition-all"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div
                                            ref={desktopContainerRef}
                                            className={`flex-1 flex flex-col rounded-xl overflow-hidden border border-white/[0.08] bg-[#070a12] ${
                                                isDesktopFullscreen ? 'fixed inset-0 z-50 rounded-none border-none' : ''
                                            }`}
                                        >
                                            {/* Floating Control Toolbar */}
                                            <div className="px-4 py-2.5 bg-[#0b101c] border-b border-white/[0.06] flex items-center justify-between gap-3 select-none flex-wrap">
                                                {/* Left Status */}
                                                <div className="flex items-center gap-3">
                                                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/20">
                                                        <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                                                        <span className="text-[10px] font-bold font-mono text-emerald-400 uppercase tracking-wider">LIVE</span>
                                                    </div>

                                                    <span className="text-xs font-mono text-slate-300 font-medium truncate max-w-[140px]">
                                                        {currentMachine.hostname}
                                                    </span>

                                                    <div className="hidden sm:flex items-center gap-2 text-[10px] font-mono text-slate-500">
                                                        <span className="px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/[0.05]">
                                                            {remoteRes.width}×{remoteRes.height}
                                                        </span>
                                                        <span className="px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/[0.05] text-cyan-400">
                                                            {liveFps} FPS
                                                        </span>
                                                        {latency > 0 && (
                                                            <span className="px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/[0.05] text-purple-400">
                                                                {latency}ms
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Center Quick Actions */}
                                                <div className="flex items-center gap-1.5">
                                                    {/* Control Mode Toggle */}
                                                    <div className="flex rounded-lg bg-white/[0.03] border border-white/[0.06] p-0.5">
                                                        <button
                                                            onClick={() => setDesktopControlMode('control')}
                                                            title="Interactive Control (Mouse & Keyboard active)"
                                                            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                                                                desktopControlMode === 'control'
                                                                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shadow-sm'
                                                                    : 'text-slate-400 hover:text-slate-200'
                                                            }`}
                                                        >
                                                            <MousePointer className="w-3 h-3" />
                                                            Control
                                                        </button>
                                                        <button
                                                            onClick={() => setDesktopControlMode('view')}
                                                            title="View Only (Mouse & Keyboard disabled)"
                                                            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-all ${
                                                                desktopControlMode === 'view'
                                                                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 shadow-sm'
                                                                    : 'text-slate-400 hover:text-slate-200'
                                                            }`}
                                                        >
                                                            <Eye className="w-3 h-3" />
                                                            View Only
                                                        </button>
                                                    </div>

                                                    <div className="h-4 w-px bg-white/[0.08] mx-1" />

                                                    {/* Special Windows OS Buttons */}
                                                    <button
                                                        onClick={() => sendDesktopCAD('cad')}
                                                        title="Send Ctrl+Alt+Del (Wake / Unlock login screen)"
                                                        className="px-2 py-1 rounded bg-blue-500/15 hover:bg-blue-500/25 text-blue-300 hover:text-white text-[11px] font-mono font-medium border border-blue-500/30 transition-all"
                                                    >
                                                        CAD
                                                    </button>
                                                    <button
                                                        onClick={() => sendDesktopCAD('start')}
                                                        title="Open Windows Start Menu"
                                                        className="px-2 py-1 rounded bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white text-[11px] font-mono font-medium border border-white/[0.06] transition-all"
                                                    >
                                                        Win
                                                    </button>
                                                    <button
                                                        onClick={() => sendDesktopCAD('showdesktop')}
                                                        title="Toggle Desktop (Win+D)"
                                                        className="px-2 py-1 rounded bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white text-[11px] font-mono font-medium border border-white/[0.06] transition-all"
                                                    >
                                                        Desktop
                                                    </button>
                                                    <button
                                                        onClick={() => sendDesktopCAD('taskmgr')}
                                                        title="Launch Task Manager"
                                                        className="px-2 py-1 rounded bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white text-[11px] font-mono font-medium border border-white/[0.06] transition-all"
                                                    >
                                                        TaskMgr
                                                    </button>
                                                    <button
                                                        onClick={() => sendDesktopCAD('lock')}
                                                        title="Lock Machine (Win+L)"
                                                        className="flex items-center gap-1 px-2 py-1 rounded bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-[11px] font-mono font-medium border border-amber-500/20 transition-all"
                                                    >
                                                        <Lock className="w-2.5 h-2.5" />
                                                        Lock
                                                    </button>

                                                    <button
                                                        onClick={() => setShowUnlockModal(!showUnlockModal)}
                                                        title="Unlock Workstation (Enter Password)"
                                                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-mono font-medium border transition-all ${
                                                            isRemoteLocked
                                                                ? 'bg-amber-500/25 hover:bg-amber-500/35 text-amber-200 border-amber-500/50 shadow-sm shadow-amber-500/20 animate-pulse'
                                                                : 'bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white border-white/[0.06]'
                                                        }`}
                                                    >
                                                        <KeyRound className="w-3 h-3 text-amber-400" />
                                                        Unlock
                                                    </button>

                                                    {isRemoteLocked && (
                                                        <button
                                                            onClick={() => setShowUnlockModal(true)}
                                                            title="Workstation is locked. Click to enter password."
                                                            className="flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 text-[10px] font-medium border border-amber-500/30 animate-pulse ml-1 cursor-pointer transition-all"
                                                        >
                                                            <Lock className="w-2.5 h-2.5" />
                                                            Locked (Click to Unlock)
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Right Options */}
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        onClick={toggleDesktopFullscreen}
                                                        title={isDesktopFullscreen ? "Exit Fullscreen" : "Fullscreen Remote Desktop"}
                                                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-200 hover:bg-white/[0.06] transition-all"
                                                    >
                                                        {isDesktopFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                                                    </button>

                                                    <button
                                                        onClick={stopDesktopSession}
                                                        title="Disconnect Remote Desktop Session"
                                                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs font-semibold border border-rose-500/20 transition-all"
                                                    >
                                                        <StopCircle className="w-3.5 h-3.5" />
                                                        Disconnect
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Canvas Screen Viewport */}
                                            <div
                                                className="flex-1 overflow-auto flex items-center justify-center p-2 bg-[#04060b] relative"
                                                onClick={() => desktopCanvasRef.current?.focus()}
                                            >
                                                <canvas
                                                    ref={desktopCanvasRef}
                                                    tabIndex={0}
                                                    className={`max-w-full max-h-full object-contain rounded shadow-2xl border border-white/[0.05] focus:outline-none focus:ring-1 focus:ring-cyan-500/30 ${
                                                        desktopControlMode === 'control' ? 'cursor-default' : 'cursor-not-allowed'
                                                    }`}
                                                    onMouseMove={(e) => handleDesktopMouseEvent(e, 'move')}
                                                    onMouseDown={(e) => handleDesktopMouseEvent(e, 'down')}
                                                    onMouseUp={(e) => handleDesktopMouseEvent(e, 'up')}
                                                    onClick={(e) => handleDesktopMouseEvent(e, 'click')}
                                                    onDoubleClick={(e) => handleDesktopMouseEvent(e, 'dblclick')}
                                                    onContextMenu={(e) => {
                                                        e.preventDefault();
                                                        handleDesktopMouseEvent(e, 'click');
                                                    }}
                                                    onWheel={handleDesktopWheel}
                                                    onKeyDown={handleDesktopKeyDown}
                                                />

                                                {/* Hint banner */}
                                                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/[0.08] text-[10px] font-mono text-slate-400 pointer-events-none opacity-70 hover:opacity-100 transition-opacity">
                                                    💡 Click screen to capture keyboard • Right-click supported • Wheel to scroll
                                                </div>

                                                {/* ── Unlock Workstation Interactive Modal / Overlay ── */}
                                                {(isRemoteLocked || showUnlockModal) && (
                                                    <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-950/80 backdrop-blur-md p-4 animate-in fade-in duration-200">
                                                        <div className="relative bg-gradient-to-b from-slate-900 to-slate-950 border border-blue-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl shadow-blue-500/15 text-center">
                                                            {/* Close button if user manually toggled when workstation is not locked */}
                                                            {!isRemoteLocked && (
                                                                <button
                                                                    onClick={() => setShowUnlockModal(false)}
                                                                    className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            )}

                                                            <div className="w-14 h-14 rounded-2xl bg-blue-500/10 border border-blue-500/25 flex items-center justify-center mx-auto mb-3 shadow-inner shadow-blue-500/10">
                                                                <Lock className="w-7 h-7 text-blue-400" />
                                                            </div>

                                                            <h3 className="text-base font-semibold text-white tracking-wide mb-1">
                                                                Windows Workstation is Locked
                                                            </h3>
                                                            <p className="text-xs text-slate-400 leading-relaxed mb-5">
                                                                Remote session connected. Enter your Windows password to unlock and resume control.
                                                            </p>

                                                            <form
                                                                onSubmit={(e) => {
                                                                    e.preventDefault();
                                                                    if (unlockPassword && !unlocking) {
                                                                        sendDesktopUnlock(unlockPassword);
                                                                    }
                                                                }}
                                                                className="space-y-3"
                                                            >
                                                                <div className="relative">
                                                                    <input
                                                                        type={showUnlockPassword ? "text" : "password"}
                                                                        value={unlockPassword}
                                                                        onChange={(e) => setUnlockPassword(e.target.value)}
                                                                        placeholder="Enter Windows password..."
                                                                        autoFocus
                                                                        disabled={unlocking}
                                                                        className="w-full bg-slate-950/90 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all pr-10"
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowUnlockPassword(!showUnlockPassword)}
                                                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                                                                        tabIndex={-1}
                                                                    >
                                                                        {showUnlockPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                                                    </button>
                                                                </div>

                                                                {unlockStatus && (
                                                                    <div className={`text-xs px-3 py-2 rounded-xl border flex items-center justify-center gap-2 ${
                                                                        unlockStatus.type === 'error'
                                                                            ? 'bg-rose-500/10 border-rose-500/25 text-rose-300'
                                                                            : unlockStatus.type === 'success'
                                                                            ? 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300'
                                                                            : 'bg-blue-500/10 border-blue-500/25 text-blue-300'
                                                                    }`}>
                                                                        {unlocking && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                                                                        <span>{unlockStatus.message}</span>
                                                                    </div>
                                                                )}

                                                                <div className="flex gap-2 pt-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => sendDesktopCAD('cad')}
                                                                        disabled={unlocking}
                                                                        title="Send Ctrl+Alt+Del to wake screen / bring up login tile"
                                                                        className="flex-1 py-2.5 px-3 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white text-xs font-mono font-medium border border-white/[0.08] transition-all flex items-center justify-center gap-1.5"
                                                                    >
                                                                        <RefreshCw className="w-3.5 h-3.5" />
                                                                        Wake / CAD
                                                                    </button>
                                                                    <button
                                                                        type="submit"
                                                                        disabled={unlocking || !unlockPassword}
                                                                        className="flex-1 py-2.5 px-3 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-xs font-semibold shadow-lg shadow-blue-500/25 transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                                                                    >
                                                                        {unlocking ? (
                                                                            <>
                                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                                Unlocking...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <KeyRound className="w-3.5 h-3.5" />
                                                                                Unlock Workstation
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Terminal Tab ── */}
                            {activeTab === 'terminal' && (
                                <div className="h-full flex flex-col gap-3">
                                    {currentMachine.status !== 'online' ? (
                                        <div className="flex-1 flex flex-col items-center justify-center text-slate-600">
                                            <WifiOff className="w-12 h-12 mb-3 opacity-30" />
                                            <span className="text-sm font-medium">Machine is offline</span>
                                            <span className="text-xs mt-1">Terminal requires an active connection</span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex items-center gap-2">
                                                {!terminalActive && (
                                                    <button
                                                        onClick={startTerminal}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 text-xs font-semibold border border-emerald-500/20 transition-all"
                                                    >
                                                        <Terminal className="w-3.5 h-3.5" />
                                                        Start Interactive Session
                                                    </button>
                                                )}
                                                <span className="text-[10px] font-mono text-slate-600">
                                                    {terminalActive ? '🟢 Interactive PTY' : '⚡ One-shot commands'}
                                                </span>
                                            </div>

                                            {/* Terminal Output */}
                                            <div
                                                ref={terminalRef}
                                                className="flex-1 min-h-[400px] bg-[#0d1117] rounded-xl border border-white/[0.08] p-4 font-mono text-xs text-emerald-300 overflow-y-auto whitespace-pre-wrap"
                                                onClick={() => terminalInputRef.current?.focus()}
                                            >
                                                {terminalOutput || '\x1b[90mType a command and press Enter...\x1b[0m\r\n'}
                                            </div>

                                            {/* Terminal Input */}
                                            <div className="flex items-center gap-2">
                                                <span className="text-cyan-400 font-mono text-sm font-bold">$</span>
                                                <input
                                                    ref={terminalInputRef}
                                                    type="text"
                                                    value={terminalInput}
                                                    onChange={e => setTerminalInput(e.target.value)}
                                                    onKeyDown={handleTerminalKeyDown}
                                                    placeholder="Enter command..."
                                                    className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm font-mono text-slate-200 placeholder-slate-600 focus:outline-none focus:border-cyan-500/40 transition-colors"
                                                />
                                                <button
                                                    onClick={sendCommand}
                                                    className="px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold transition-all"
                                                >
                                                    Run
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* ── Resources Tab ── */}
                            {activeTab === 'resources' && (
                                <div className="space-y-5">
                                    {machineDetail?.resourceHistory?.length > 0 ? (
                                        <>
                                            {/* CPU Chart */}
                                            <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <Cpu className="w-4 h-4 text-cyan-400" />
                                                    <span className="text-sm font-semibold text-slate-300">CPU Usage</span>
                                                </div>
                                                <ResponsiveContainer width="100%" height={180}>
                                                    <AreaChart data={machineDetail.resourceHistory}>
                                                        <defs>
                                                            <linearGradient id="cpuGrad" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} />
                                                                <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                                        <XAxis dataKey="timestamp" tick={false} stroke="rgba(255,255,255,0.06)" />
                                                        <YAxis domain={[0, 100]} stroke="rgba(255,255,255,0.06)" tick={{ fill: '#64748b', fontSize: 10 }} />
                                                        <Tooltip
                                                            contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }}
                                                            labelStyle={{ color: '#94a3b8' }}
                                                            formatter={(v) => [`${v.toFixed(1)}%`, 'CPU']}
                                                        />
                                                        <Area type="monotone" dataKey="cpuUsage" stroke="#06b6d4" fill="url(#cpuGrad)" strokeWidth={2} />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </div>

                                            {/* Memory Chart */}
                                            <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <MemoryStick className="w-4 h-4 text-purple-400" />
                                                    <span className="text-sm font-semibold text-slate-300">Memory Usage</span>
                                                </div>
                                                <ResponsiveContainer width="100%" height={180}>
                                                    <AreaChart data={machineDetail.resourceHistory}>
                                                        <defs>
                                                            <linearGradient id="memGrad" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                                                                <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                                        <XAxis dataKey="timestamp" tick={false} stroke="rgba(255,255,255,0.06)" />
                                                        <YAxis domain={[0, 100]} stroke="rgba(255,255,255,0.06)" tick={{ fill: '#64748b', fontSize: 10 }} />
                                                        <Tooltip
                                                            contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }}
                                                            labelStyle={{ color: '#94a3b8' }}
                                                            formatter={(v) => [`${v.toFixed(1)}%`, 'Memory']}
                                                        />
                                                        <Area type="monotone" dataKey="memoryUsage" stroke="#a855f7" fill="url(#memGrad)" strokeWidth={2} />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </div>

                                            {/* Disk Chart */}
                                            <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4">
                                                <div className="flex items-center gap-2 mb-3">
                                                    <HardDrive className="w-4 h-4 text-amber-400" />
                                                    <span className="text-sm font-semibold text-slate-300">Disk Usage</span>
                                                </div>
                                                <ResponsiveContainer width="100%" height={180}>
                                                    <AreaChart data={machineDetail.resourceHistory}>
                                                        <defs>
                                                            <linearGradient id="diskGrad" x1="0" y1="0" x2="0" y2="1">
                                                                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                                                                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                                                            </linearGradient>
                                                        </defs>
                                                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                                                        <XAxis dataKey="timestamp" tick={false} stroke="rgba(255,255,255,0.06)" />
                                                        <YAxis domain={[0, 100]} stroke="rgba(255,255,255,0.06)" tick={{ fill: '#64748b', fontSize: 10 }} />
                                                        <Tooltip
                                                            contentStyle={{ backgroundColor: '#0f172a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }}
                                                            labelStyle={{ color: '#94a3b8' }}
                                                            formatter={(v) => [`${v.toFixed(1)}%`, 'Disk']}
                                                        />
                                                        <Area type="monotone" dataKey="diskUsage" stroke="#f59e0b" fill="url(#diskGrad)" strokeWidth={2} />
                                                    </AreaChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center py-20 text-slate-600">
                                            <BarChart3 className="w-12 h-12 mb-3 opacity-30" />
                                            <span className="text-sm font-medium">No resource data yet</span>
                                            <span className="text-xs mt-1">Data will appear after the agent sends heartbeats</span>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Settings Tab ── */}
                            {activeTab === 'settings' && (
                                <div className="space-y-4">
                                    {/* Tags */}
                                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Tag className="w-4 h-4 text-slate-400" />
                                            <span className="text-sm font-semibold text-slate-300">Tags</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {(currentMachine.tags || []).map((tag, i) => (
                                                <span key={i} className="px-2 py-1 rounded-lg text-[10px] font-mono bg-white/[0.04] text-slate-400 border border-white/[0.06]">
                                                    {tag}
                                                </span>
                                            ))}
                                            {(!currentMachine.tags || currentMachine.tags.length === 0) && (
                                                <span className="text-xs text-slate-600">No tags assigned</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Rotate Token */}
                                    <div className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <RotateCw className="w-4 h-4 text-amber-400" />
                                                    <span className="text-sm font-semibold text-slate-300">Rotate Machine Token</span>
                                                </div>
                                                <p className="text-xs text-slate-500">Generate a new authentication token. The agent will need to be reconfigured.</p>
                                            </div>
                                            <button
                                                onClick={rotateToken}
                                                className="px-3 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 text-amber-400 text-xs font-semibold border border-amber-500/20 transition-all"
                                            >
                                                Rotate
                                            </button>
                                        </div>
                                    </div>

                                    {/* Remove Machine */}
                                    <div className="rounded-xl bg-rose-500/5 border border-rose-500/10 p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Trash2 className="w-4 h-4 text-rose-400" />
                                                    <span className="text-sm font-semibold text-rose-300">Remove Machine</span>
                                                </div>
                                                <p className="text-xs text-slate-500">Permanently remove this machine and revoke its token.</p>
                                            </div>
                                            {showDeleteConfirm ? (
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={deleteMachine}
                                                        className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold transition-all"
                                                    >
                                                        Confirm
                                                    </button>
                                                    <button
                                                        onClick={() => setShowDeleteConfirm(false)}
                                                        className="px-3 py-1.5 rounded-lg bg-white/[0.06] text-slate-400 text-xs font-semibold transition-all hover:bg-white/[0.10]"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => setShowDeleteConfirm(true)}
                                                    className="px-3 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 text-rose-400 text-xs font-semibold border border-rose-500/20 transition-all"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* ═══════════════════════════════════════════════════════════════════ */}
            {/* 🎯 Enrollment Modal                                               */}
            {/* ═══════════════════════════════════════════════════════════════════ */}
            {showEnrollModal && enrollmentData && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
                    <div className="w-full max-w-2xl bg-[#0f1628] rounded-2xl border border-white/[0.08] shadow-2xl shadow-black/50 overflow-hidden">
                        {/* Modal Header */}
                        <div className="p-5 border-b border-white/[0.06] bg-gradient-to-r from-cyan-500/5 to-transparent">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
                                        <Shield className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-slate-100">Machine Enrollment Token</h3>
                                        <p className="text-xs text-slate-500 mt-0.5">Run the install command on the target machine</p>
                                    </div>
                                </div>
                                <button onClick={() => { setShowEnrollModal(false); fetchMachines(); }} className="text-slate-500 hover:text-slate-300">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
                            {/* Token */}
                            <div>
                                <label className="text-[10px] font-mono text-slate-500 uppercase block mb-1.5">Enrollment Token (one-time use)</label>
                                <div className="flex items-center gap-2">
                                    <code className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs font-mono text-amber-400 truncate">
                                        {enrollmentData.enrollmentToken}
                                    </code>
                                    <button
                                        onClick={() => copyToClipboard(enrollmentData.enrollmentToken, 'enroll-token')}
                                        className="p-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.10] text-slate-400 hover:text-slate-200 transition-all flex items-center gap-1.5"
                                        title="Copy Token"
                                    >
                                        {copiedKey === 'enroll-token' || copiedToken ? (
                                            <>
                                                <Check className="w-4 h-4 text-emerald-400" />
                                                <span className="text-[11px] text-emerald-400 font-mono">Copied!</span>
                                            </>
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Target Machine Network Mode Selector */}
                            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3.5 space-y-2.5">
                                <label className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block font-semibold">
                                    Target Machine Location (Server Address)
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setEnrollServerMode('public')}
                                        className={`flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all ${
                                            enrollServerMode === 'public'
                                                ? 'bg-cyan-500/15 border-cyan-500/40 text-cyan-300 shadow-sm'
                                                : 'bg-white/[0.02] border-white/[0.06] text-slate-400 hover:text-slate-200'
                                        }`}
                                    >
                                        <Globe className="w-4 h-4 flex-shrink-0 text-cyan-400" />
                                        <div className="min-w-0">
                                            <p className="text-xs font-semibold">Secure Cloud / Public Domain</p>
                                            <p className="text-[10px] font-mono opacity-80 truncate text-emerald-400">https://sentinel.o365itguys.com (TLS)</p>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setEnrollServerMode('lan')}
                                        className={`flex items-center gap-2 p-2.5 rounded-lg border text-left transition-all ${
                                            enrollServerMode === 'lan'
                                                ? 'bg-blue-500/15 border-blue-500/40 text-blue-300 shadow-sm'
                                                : 'bg-white/[0.02] border-white/[0.06] text-slate-400 hover:text-slate-200'
                                        }`}
                                    >
                                        <Network className="w-4 h-4 flex-shrink-0 text-blue-400" />
                                        <div className="min-w-0">
                                            <p className="text-xs font-semibold">Office LAN PC (Encrypted)</p>
                                            <p className="text-[10px] font-mono opacity-80 truncate text-emerald-400">https://sentinel.o365itguys.com (TLS)</p>
                                        </div>
                                    </button>
                                </div>

                                {enrollServerMode === 'custom' && (
                                    <input
                                        type="text"
                                        value={enrollCustomUrl}
                                        onChange={(e) => setEnrollCustomUrl(e.target.value)}
                                        placeholder="https://sentinel.o365itguys.com"
                                        className="w-full bg-black/40 border border-white/[0.1] rounded-lg px-3 py-1.5 text-xs text-white font-mono outline-none focus:border-cyan-500/50"
                                    />
                                )}
                            </div>

                            {/* Install Instructions */}
                            {(() => {
                                const selectedHost = enrollServerMode === 'public'
                                    ? 'https://sentinel.o365itguys.com'
                                    : enrollServerMode === 'lan'
                                    ? 'https://sentinel.o365itguys.com'
                                    : (enrollCustomUrl || 'https://sentinel.o365itguys.com').replace(/\/$/, '');
                                const token = enrollmentData.enrollmentToken;

                                const instructions = [
                                    {
                                        os: 'Windows (PowerShell as Admin)',
                                        icon: Monitor,
                                        cmd: `powershell -ExecutionPolicy Bypass -Command "& ([scriptblock]::Create((Invoke-RestMethod '${selectedHost}/api/soc/machines/install-script/windows'))) -ServerUrl '${selectedHost}' -Token '${token}'"`,
                                        color: 'blue'
                                    },
                                    {
                                        os: 'Linux (bash as root)',
                                        icon: Server,
                                        cmd: `curl -sSL ${selectedHost}/api/soc/machines/install-script/linux | sudo bash -s -- --server "${selectedHost}" --token "${token}"`,
                                        color: 'emerald'
                                    },
                                    {
                                        os: 'macOS (bash as root)',
                                        icon: Laptop,
                                        cmd: `curl -sSL ${selectedHost}/api/soc/machines/install-script/macos | sudo bash -s -- --server "${selectedHost}" --token "${token}"`,
                                        color: 'slate'
                                    }
                                ];

                                return instructions.map(item => (
                                    <div key={item.os} className="rounded-xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
                                        <div className={`flex items-center gap-2 px-4 py-2 bg-${item.color}-500/5 border-b border-white/[0.04]`}>
                                            <item.icon className={`w-3.5 h-3.5 text-${item.color}-400`} />
                                            <span className="text-xs font-semibold text-slate-300">{item.os}</span>
                                        </div>
                                        <div className="p-3 flex items-start gap-2">
                                            <code className="flex-1 text-[10px] font-mono text-slate-400 break-all leading-relaxed">
                                                {item.cmd}
                                            </code>
                                            <button
                                                onClick={() => copyToClipboard(item.cmd, item.os)}
                                                className="p-1.5 rounded bg-white/[0.06] hover:bg-white/[0.10] text-slate-400 hover:text-slate-200 transition-all flex-shrink-0 flex items-center gap-1"
                                                title="Copy command"
                                            >
                                                {copiedKey === item.os ? (
                                                    <>
                                                        <Check className="w-3.5 h-3.5 text-emerald-400" />
                                                        <span className="text-[10px] text-emerald-400 font-mono">Copied!</span>
                                                    </>
                                                ) : (
                                                    <Copy className="w-3.5 h-3.5" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                ));
                            })()}

                            <div className="rounded-lg bg-amber-500/5 border border-amber-500/10 p-3">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                                    <p className="text-xs text-amber-300/80">
                                        This token can only be used once. After enrollment, the machine will receive a permanent authentication credential.
                                        Keep this token secure — anyone with it can enroll a machine to your dashboard.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t border-white/[0.06] flex justify-end">
                            <button
                                onClick={() => { setShowEnrollModal(false); fetchMachines(); }}
                                className="px-4 py-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.10] text-slate-300 text-sm font-semibold transition-all"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════════ */}
            {/* 🔑 Rotated Token Modal                                            */}
            {/* ═══════════════════════════════════════════════════════════════════ */}
            {showTokenModal && rotatedToken && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
                    <div className="w-full max-w-lg bg-[#0f1628] rounded-2xl border border-white/[0.08] shadow-2xl p-6 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-500/10 text-amber-400">
                                <RotateCw className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-slate-100">New Machine Token</h3>
                                <p className="text-xs text-slate-500 mt-0.5">Update the agent configuration with this new token</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <code className="flex-1 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-xs font-mono text-amber-400 break-all">
                                {rotatedToken}
                            </code>
                            <button
                                onClick={() => copyToClipboard(rotatedToken, 'rotate-token')}
                                className="p-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.10] text-slate-400 hover:text-slate-200 transition-all flex items-center gap-1.5"
                                title="Copy Token"
                            >
                                {copiedKey === 'rotate-token' ? (
                                    <>
                                        <Check className="w-4 h-4 text-emerald-400" />
                                        <span className="text-[11px] text-emerald-400 font-mono">Copied!</span>
                                    </>
                                ) : (
                                    <Copy className="w-4 h-4" />
                                )}
                            </button>
                        </div>

                        <div className="rounded-lg bg-rose-500/5 border border-rose-500/10 p-3">
                            <p className="text-xs text-rose-300/80">
                                ⚠️ The agent has been disconnected and must be reconfigured with this new token. This token is shown only once.
                            </p>
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={() => { setShowTokenModal(false); setRotatedToken(null); }}
                                className="px-4 py-2 rounded-lg bg-white/[0.06] hover:bg-white/[0.10] text-slate-300 text-sm font-semibold transition-all"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
